import React from 'react';
import { RecommendedSuite, Page, NavParams } from '@types';
import { SUITE_DETAILS } from '../constants';

interface RecommendedSuitesDisplayProps {
    suites: RecommendedSuite[];
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
    contextCountry?: string;
}

const RecommendedSuitesDisplay: React.FC<RecommendedSuitesDisplayProps> = ({ suites, onNavigate, contextCountry }) => {
    
    // Icon info for specific marketing suites
    const threatGroupSuiteInfo = {
        name: "Threat Group OSINT",
        description: "Investigate state-sponsored and non-state threat actors globally or by country.",
        icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" /></svg>,
        color: { bg: 'bg-red-100', text: 'text-red-600' }
    };

    return (
        <div className="mt-8 pt-6 border-t border-slate-200">
            <h4 className="font-semibold text-slate-700 mb-4 text-center text-xs uppercase tracking-widest">Recommended Strategic Next Steps</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {suites.map((rec, index) => {
                    // 1. Get Branding Info
                    const suiteInfo = Object.values(SUITE_DETAILS).find(s => s.name === rec.suiteName);
                    let displayInfo = suiteInfo;

                    if (rec.suiteName === 'GSOC Live Threat Monitoring') displayInfo = SUITE_DETAILS.gsoc;
                    if (rec.suiteName === 'Threat Group OSINT') displayInfo = threatGroupSuiteInfo as any;

                    // 2. Combined Navigation Logic (Revenue + Standard)
                    const handleNav = () => {
                        if (!onNavigate) return;
                        if (rec.suiteName === 'Auto-Architect SRM Framework') {
                            onNavigate({ page: 'srm_command_center', params: { view: 'architect' } });
                        } else if (rec.suiteName === 'Security Risk Management') {
                            onNavigate({ page: 'suiteDashboard', params: { suiteName: 'Security Risk Management' } });
                        } else if (rec.suiteName === 'GSOC Live Threat Monitoring') {
                            onNavigate({ page: 'gsocDashboard' });
                        } else if (rec.suiteName === 'Threat Group OSINT') {
                            onNavigate({ page: 'gtg_dashboard', params: { preselectedCountry: contextCountry } });
                        } else if (rec.suiteName && rec.suiteName !== 'N/A') {
                            onNavigate({ page: 'suiteDashboard', params: { suiteName: rec.suiteName } });
                        }
                    };

                    if (!displayInfo && rec.suiteName !== 'Auto-Architect SRM Framework') return null;

                    return (
                        <div 
                            key={index} 
                            onClick={handleNav}
                            className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex flex-col hover:shadow-md hover:border-blue-400 hover:scale-[1.01] transition-all cursor-pointer group/card"
                        >
                            <div className="flex items-center mb-2">
                                <div className={`p-2 rounded-full ${displayInfo?.color.bg || 'bg-blue-100'} ${displayInfo?.color.text || 'bg-blue-600'} mr-3`}>
                                    {React.isValidElement(displayInfo?.icon) ? React.cloneElement(displayInfo.icon as React.ReactElement<any>, { className: 'h-5 w-5' }) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                    )}
                                </div>
                                <h5 className="font-bold text-slate-800">{rec.suiteName}</h5>
                            </div>
                            <p className="text-sm text-slate-600 flex-grow mb-3">{rec.justification}</p>
                            <div
                                className="text-sm font-semibold text-blue-600 hover:text-blue-700 self-start group flex items-center"
                            >
                                Go to Suite <span className="transition-transform group-hover/card:translate-x-1 ml-1">&rarr;</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RecommendedSuitesDisplay;
